import React, {
    useContext,
    useMemo,
    useState,
    useEffect,
    useCallback,
} from 'react';
import './PortfolioSummaryGrid.scss';
import AppContext from 'app/AppContext';
import { Grid, GridApi } from '@instinet-analytics/semantic-ui-components';
import { PubSub } from '@instinet-analytics/core-lib';
import {
    PortfolioSummaryColumn,
    portfolioSummaryColumnDefinitions,
} from './portfolioSummaryGridColumns';
import { AggregationField } from '../aggregation/fields/aggregationFieldPortfolio';
import { getAggregationFieldHeaderText } from '../aggregation/fields/aggregationFieldDropdownItems';
import {
    getPortfolioSummaryDataByGroup,
    PortfolioSummaryDataByGroupItem,
} from '../aggregationByGroup/portfolioSummaryDataByGroup';
import {
    useSelectCombineSellsAndShorts,
    useShowBuysAsPositive,
} from 'app/store/appSettings/selectors';
import { useDispatchUpdateWidgetColumnWidth } from 'widgets/store/grid/columnWidth/dispatcher';
import { applyColumnWidths } from 'widgets/store/grid/columnWidth/utils';
import { useDispatchUpdatePortfolioSummaryVisibleColumns } from '../store/instances/configuration/dispatcher';
import { usePortfolioSummaryConfigurationSelector } from '../store/instances/configuration/selectors';
import { filterToVisibleColumns } from '@instinet-analytics/semantic-ui-components';
import { useSelectPortfolioSummaryAggregatedData } from '../store/data/selectors';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

export interface PortfolioSummaryGridProps {
    changedFilterRef: PubSub<void>;
    aggregationField: AggregationField;
    onGridApiSet: (gridApi: GridApi) => void;
    onSelectedValuesChanged: (values: string[]) => void;
}

export default function PortfolioSummaryGrid(props: PortfolioSummaryGridProps) {
    const {
        aggregationField,
        changedFilterRef,
        onGridApiSet,
        onSelectedValuesChanged,
    } = props;

    const data = useSelectPortfolioSummaryAggregatedData(aggregationField);
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

    const appContext = useContext(AppContext);
    const { currencyFormat } = appContext;

    const { visibleColumns, columnWidths } =
        usePortfolioSummaryConfigurationSelector();

    const dispatchSetColumnWidth = useDispatchUpdateWidgetColumnWidth();

    const dispatchUpdateVisibleColumns =
        useDispatchUpdatePortfolioSummaryVisibleColumns();

    const showBuysAsPositive = useShowBuysAsPositive();
    const combineSellsAndShorts = useSelectCombineSellsAndShorts();
    const headerText = getAggregationFieldHeaderText(
        aggregationField,
        currencyFormat
    );

    const [gridApi, setApi] = useState<GridApi | undefined>();
    const onGridReady = (api: GridApi) => {
        setApi(api);
        onGridApiSet(api);
    };

    const useWidgetEditedColumnNamesProps = useWidgetEditedColumnNames();

    useEffect(() => {
        if (!gridApi) {
            return;
        }
        changedFilterRef.subscribe('Grid', () => {
            setSelectedRowIds([]);
        });
    }, [setSelectedRowIds, changedFilterRef, gridApi]);

    const dataByGroup = useMemo(
        () =>
            getPortfolioSummaryDataByGroup(aggregationField, data ?? {}, {
                showBuysAsPositive,
                combineSellsAndShorts,
                exchangeRate: currencyFormat.exchangeRate ?? 1,
                addTotalGroup: true,
            }),

        [
            aggregationField,
            currencyFormat.exchangeRate,
            data,
            showBuysAsPositive,
            combineSellsAndShorts,
        ]
    );

    useEffect(() => {
        // Auto size columns when the aggregation field has changed and we get some data
        // We set suppressColumnVirtualisation on the grid to ensure this works for all columns
        if (gridApi && dataByGroup.length > 0) {
            gridApi.autoSizeAllColumns();
        }
    }, [aggregationField, gridApi, dataByGroup.length]);

    const handleSelectedValuesChanged = useCallback(
        (rowIds: string[]) => {
            onSelectedValuesChanged(
                rowIds.map(
                    (r) => dataByGroup.find((i) => i.index === r)!.groupName
                )
            );
            setSelectedRowIds(rowIds);
        },
        [onSelectedValuesChanged, setSelectedRowIds, dataByGroup]
    );

    useEffect(() => {
        handleSelectedValuesChanged([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aggregationField]);

    const allColumnDefs = portfolioSummaryColumnDefinitions(
        appContext,
        headerText,
        aggregationField
    );
    const columnDefs = filterToVisibleColumns(allColumnDefs, visibleColumns);
    applyColumnWidths(columnDefs, columnWidths);

    const onColumnResized = (columnId: string, width: number) => {
        dispatchSetColumnWidth(columnId, width);
    };

    const onColumnMoved = (columns: string[]) =>
        dispatchUpdateVisibleColumns(columns as PortfolioSummaryColumn[]);

    const isRowSelectable = (item: PortfolioSummaryDataByGroupItem) => {
        return !item.selectionDisabled;
    };

    return (
        <div className="PortfolioSummaryGrid">
            {dataByGroup.length > 0 && (
                <Grid
                    columnDefs={columnDefs}
                    rowData={dataByGroup}
                    getRowDataId={(r) => r.index}
                    autoSizeColumns={true}
                    suppressColumnVirtualisation={true}
                    columnsExcludedFromColumnNameEditing={['groupName']}
                    getRowClass={getRowClass}
                    onColumnResized={onColumnResized}
                    onColumnMoved={onColumnMoved}
                    onGridReady={onGridReady}
                    selectedRowIds={selectedRowIds}
                    onSelectedRowIdsChanged={handleSelectedValuesChanged}
                    isRowSelectable={isRowSelectable}
                    {...useWidgetEditedColumnNamesProps}
                />
            )}
        </div>
    );
}

function getRowClass({
    data: rowData,
}: {
    data: PortfolioSummaryDataByGroupItem;
}): string {
    return 'group-by-' + rowData.groupName;
}
